import { Component, type ErrorInfo, type ReactNode } from 'react';
import { appendCapturedError } from '@/hooks/useClientErrorCapture';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
  componentStack: string;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null, componentStack: '' };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error, componentStack: '' };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.setState({ componentStack: info.componentStack ?? '' });
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info.componentStack);
    // Persist to the client-error buffer so it shows up in
    //   localStorage['pcb-client-errors'] and window.__PCB_ERRORS__.
    appendCapturedError({
      when: new Date().toISOString(),
      kind: 'react-boundary',
      message: error.message,
      stack: error.stack ?? '',
      componentStack: info.componentStack ?? '',
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    });
  }

  reset = (): void => {
    this.setState({ error: null, componentStack: '' });
  };

  copyToClipboard = async (): Promise<void> => {
    const e = this.state.error;
    if (!e) return;
    const payload = [
      `[${new Date().toISOString()}] ${e.name}: ${e.message}`,
      '',
      'Stack:',
      e.stack ?? '(no stack)',
      '',
      'Component stack:',
      this.state.componentStack || '(none)',
      '',
      `URL: ${typeof window !== 'undefined' ? window.location.href : ''}`,
      `UA:  ${typeof navigator !== 'undefined' ? navigator.userAgent : ''}`,
    ].join('\n');
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(payload);
      } else {
        // Fallback - older browsers / iframe denials.
        const ta = document.createElement('textarea');
        ta.value = payload;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        ta.remove();
      }
    } catch {
      // eslint-disable-next-line no-console
      console.error('Could not copy error. Full text logged above.');
    }
  };

  render(): ReactNode {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback(this.state.error, this.reset);
      const { error, componentStack } = this.state;
      return (
        <div
          style={{
            margin: 'var(--sp-9)',
            padding: 'var(--sp-10)',
            borderRadius: 'var(--r-lg)',
            background: 'var(--err-bg)',
            color: 'var(--err-text)',
            border: '1px solid var(--pcb-red)',
            maxWidth: 900,
          }}
        >
          <h2 style={{ color: 'var(--pcb-red)', marginBottom: 'var(--sp-5)' }}>Something went wrong.</h2>
          <p style={{ color: 'var(--ink-900)', marginBottom: 'var(--sp-5)', fontWeight: 600, fontFamily: 'var(--font-mono)' }}>
            {error.name}: {error.message}
          </p>
          <div style={{ display: 'flex', gap: 'var(--sp-5)', marginBottom: 'var(--sp-7)' }}>
            <button type="button" className="btn btn-ghost" onClick={this.reset}>Retry</button>
            <button type="button" className="btn btn-ghost" onClick={() => { void this.copyToClipboard(); }}>Copy error</button>
          </div>
          <details style={{ background: 'var(--surface-card)', padding: 'var(--sp-5)', borderRadius: 'var(--r-md)' }}>
            <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--ink-700)' }}>Stack trace</summary>
            <pre style={{ fontSize: 11, fontFamily: 'var(--font-mono)', whiteSpace: 'pre-wrap', color: 'var(--ink-700)', marginTop: 8 }}>
{error.stack ?? '(no stack)'}
            </pre>
            {componentStack && (
              <>
                <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 12, fontWeight: 600 }}>React component stack</div>
                <pre style={{ fontSize: 11, fontFamily: 'var(--font-mono)', whiteSpace: 'pre-wrap', color: 'var(--ink-700)' }}>
{componentStack}
                </pre>
              </>
            )}
          </details>
          <p style={{ fontSize: 11, color: 'var(--ink-500)', marginTop: 'var(--sp-6)' }}>
            Every crash is now persisted to <code>localStorage['pcb-client-errors']</code> and streamed to the server via
            <code> /v3/events </code> (eventType <code>client.error.captured</code>). Inspect via DevTools console:
            <code style={{ marginLeft: 6 }}>window.__PCB_ERRORS__</code>
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}
